import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaCalendar } from "react-icons/fa";
import classStudents from "../../assets/gallery/education/classstudents.png";
import classPupils from "../../assets/gallery/education/classpupils.jpeg";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import techTraining3 from "../../assets/gallery/education/tech-training3.jpg";
import techTraining4 from "../../assets/gallery/education/tech-training4.jpg";
import techTraining5 from "../../assets/gallery/education/tech-training5.png";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import roadConstruction1 from "../../assets/gallery/community-projects/road-construction1.jpeg";
import roadConstruction2 from "../../assets/gallery/community-projects/road-construction2.jpeg";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import healthOutreach2 from "../../assets/gallery/health/health-outreach2.jpeg";
import healthOutreach3 from "../../assets/gallery/health/health-outreach3.png";
import hospital1 from "../../assets/gallery/health/hospital1.jpeg";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function OurStory() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const milestones = [
    {
      year: "2010",
      title: "Foundation Established",
      description: "Ojoto Union North America was officially established as the umbrella organization for Ojotorians in the United States and Canada.",
      image: groupEvent
    },
    {
      year: "2012",
      title: "First Scholarship Program",
      description: "Launched our first scholarship program, supporting students in their educational journey across North America.",
      image: scholarship
    },
    {
      year: "2015",
      title: "Community Development Initiative",
      description: "Initiated our first major community development project in Ojoto, focusing on infrastructure and education.",
      image: roadConstruction1
    },
    {
      year: "2018",
      title: "Health Outreach Program",
      description: "Established health outreach programs, organizing medical missions and supporting healthcare infrastructure in our hometown.",
      image: healthOutreach1
    },
    {
      year: "2020",
      title: "Digital Platform Launch",
      description: "Launched our online platform to better connect members, streamline services, and expand our reach across North America.",
      image: techTraining1
    },
    {
      year: "2024",
      title: "500+ Members Milestone",
      description: "Reached over 500 active members, with programs impacting thousands of lives in our community and hometown.",
      image: roadConstruction2
    }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Our Story"
        subtitle="From humble beginnings to a thriving community of 500+ members, discover the journey of Ojoto Union North America."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("ourStory")}
      />

      {/* Introduction Section */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Building Bridges Across Continents
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Since our founding in 2010, Ojoto Union North America has grown from a small group of dedicated individuals 
                to a thriving community of over 500 members. Our journey has been marked by milestones in education, 
                healthcare, community development, and cultural preservation.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Timeline Section - Zig-Zag Pattern */}
      <section className="section bg-gray-50" style={{ overflow: 'visible', paddingBottom: '80px' }}>
        <div className="section-container" style={{ overflow: 'visible' }}>
          <div className="max-w-6xl mx-auto relative" style={{ overflow: 'visible', paddingTop: '40px', paddingBottom: '40px' }}>
            {/* Vertical Timeline Line - Only visible on desktop */}
            <div 
              className="hidden md:block absolute left-1/2 transform -translate-x-1/2 bg-gray-300"
              style={{
                top: '0',
                bottom: '0',
                width: '2px',
                height: '100%',
                zIndex: 1
              }}
            ></div>

            {/* Milestones */}
            {milestones.map((milestone, index) => {
              const isEven = index % 2 === 0;
              return (
                <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                  <div className="mb-16 last:mb-0 relative">
                    <div className={`flex flex-col md:flex-row items-center w-full ${
                      isEven ? 'md:flex-row-reverse' : ''
                    }`}>
                      {/* Left/Right Card Section */}
                      <div className={`w-full md:w-1/2 ${isEven ? 'md:pr-8 md:pl-0' : 'md:pl-8 md:pr-0'} mb-4 md:mb-0`}>
                        <div className="card" style={{ overflow: 'visible' }}>
                          {/* Year and Calendar Icon */}
                          <div className="flex items-center gap-3 mb-4">
                            <FaCalendar className="text-[#1E40AF] text-xl" />
                            <span 
                              className="text-lg font-semibold"
                              style={{
                                fontFamily: "'Open Sans', system-ui, sans-serif",
                                fontWeight: 600,
                                color: '#1F2937'
                              }}
                            >
                              {milestone.year}
                            </span>
                          </div>
                          
                          {/* Title */}
                          <h3 
                            className="text-xl font-bold mb-3"
                            style={{
                              fontFamily: "'Open Sans', system-ui, sans-serif",
                              fontWeight: 700,
                              color: '#1F2937',
                              lineHeight: 1.3
                            }}
                          >
                            {milestone.title}
                          </h3>
                          
                          {/* Description */}
                          <p 
                            className="text-gray-600 mb-4"
                            style={{
                              fontFamily: "'Open Sans', system-ui, sans-serif",
                              fontWeight: 400,
                              color: '#374151',
                              lineHeight: 1.6
                            }}
                          >
                            {milestone.description}
                          </p>
                          
                          {/* Image - Fully Visible */}
                          <div className="w-full" style={{ overflow: 'visible', padding: '4px' }}>
                            <img
                              src={milestone.image}
                              alt={milestone.title}
                              className="w-full rounded-md card-image"
                              style={{
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                                objectFit: 'cover',
                                width: '100%',
                                height: '250px',
                                display: 'block'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Center Timeline Circle - Only visible on desktop */}
                      <div 
                        className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 rounded-full flex items-center justify-center text-white font-bold z-10"
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#1E40AF',
                          top: '20px',
                          border: '3px solid #FFFFFF',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {index + 1}
                      </div>

                      {/* Empty space for opposite side on desktop */}
                      <div className="hidden md:block w-1/2"></div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Be Part of Our Story
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Join us as we continue to build bridges, create opportunities, and make a lasting impact in our community.
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
